import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import {
  Box, useTheme, IconButton, Dialog, Grid, FormControl, InputLabel,
  OutlinedInput, FormHelperText, Select, MenuItem, TextField, Button,
  Switch, Slide, Link, DialogContent, DialogTitle,
  DialogActions, Paper, Typography, Chip, Stack, Avatar, Tooltip,
  InputAdornment, alpha, Divider,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { GetUsersList, PatchUser, PatchUserPassword } from "api/users";
import { GetStationDetailById, GetStationList } from "api/stations";
import { getAllCustomerGroups } from "api/customerGroup";
import { GetRoleList } from "api/role";
import { GetAgentList } from "api/agent";
import ChatIcon from "@mui/icons-material/Chat";
import { playCorrectSound, playIncorrectSound } from "utils";
import { user as currentUser } from "localStorage";
import NewUser from "./newUser";

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const ROLE_COLORS = {
  admin:   { bg: "#7c3aed", label: "Admin" },
  manager: { bg: "#0369a1", label: "Manager" },
  staff:   { bg: "#047857", label: "Staff" },
  user:    { bg: "#374151", label: "User" },
  agent:   { bg: "#b45309", label: "Agent" },
};

const getRoleStyle = (role) => ROLE_COLORS[role?.toLowerCase()] || { bg: "#374151", label: role };

const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

const UserCard = ({ user, onEdit, theme }) => {
  const { bg, label } = getRoleStyle(user.role);
  const defaultStation = user.stations?.find((s) => s.default);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, borderRadius: 2,
        borderColor: user.validflag ? theme.palette.divider : theme.palette.error.light,
        backgroundColor: user.validflag
          ? theme.palette.background.paper
          : alpha(theme.palette.error.main, 0.04),
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 4 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: bg, fontSize: "1rem", fontWeight: 700, flexShrink: 0 }}>
          {getInitials(user.name)}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
            <Typography variant="body1" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>{user.name}</Typography>
            {!user.validflag && <CancelIcon color="error" sx={{ fontSize: 16 }} />}
            {user.validflag && <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />}
          </Stack>
          <Chip
            label={label}
            size="small"
            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: bg, color: "#fff", mt: 0.25 }}
          />
        </Box>
        <Tooltip title="Edit user">
          <IconButton size="small" onClick={() => onEdit(user)} sx={{ flexShrink: 0, "&:hover": { color: theme.palette.warning.main } }}>
            <ModeEditOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {/* Contact info */}
      <Stack spacing={0.75} flex={1}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <EmailIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" noWrap title={user.email}>{user.email}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <WhatsAppIcon sx={{ fontSize: 14, color: "success.main", flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">{user.whatsapp}</Typography>
        </Stack>
      </Stack>

      {/* Stations */}
      {user.stations?.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Stations</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {user.stations.map((s) => (
              <Chip
                key={s.id}
                label={s.id}
                size="small"
                variant={s.default ? "filled" : "outlined"}
                color={s.default ? "secondary" : "default"}
                sx={{ height: 18, fontSize: "0.65rem" }}
                title={s.default ? `${s.name} (Default)` : s.name}
              />
            ))}
          </Stack>
          {defaultStation && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: "block" }}>
              Default: {defaultStation.name}
            </Typography>
          )}
        </Box>
      )}

      {/* WhatsApp Instances */}
      {user.whatsappInstances?.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>WhatsApp Instances</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {user.whatsappInstances.map((w) => (
              <Chip
                key={w.id}
                icon={<ChatIcon sx={{ fontSize: "0.8rem !important" }} />}
                label={w.name}
                size="small"
                variant={w.default ? "filled" : "outlined"}
                color={w.default ? "secondary" : "default"}
                sx={{ height: 18, fontSize: "0.65rem" }}
                title={w.default ? `${w.name} (Default)` : w.name}
              />
            ))}
          </Stack>
        </Box>
      )}

      {user.remark && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, fontStyle: "italic" }} noWrap title={user.remark}>
          {user.remark}
        </Typography>
      )}
    </Paper>
  );
};

const UserList = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  // edit form state
  const [userId, setUserId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [stations, setStations] = useState([]);
  const [stationList, setStationList] = useState(null);
  const [selectedStation, setSelectedStation] = useState("");
  const [whatsappInstances, setWhatsappInstances] = useState([]);
  const [instanceList, setInstanceList] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [remark, setRemark] = useState("");
  const [validflag, setValidflag] = useState(true);
  const [whatsappChatFooter, setWhatsappChatFooter] = useState(true);
  const isAdmin = currentUser()?.role === "admin"; // only an admin may change the chat footer
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [roles, setRoles] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [agentList, setAgentList] = useState([]);
  const [allowedSeeAllCustomers, setAllowedSeeAllCustomers] = useState(false);
  const [allowedSeeAllPackages, setAllowedSeeAllPackages] = useState(false);

  const loadUsers = useCallback(() => {
    GetUsersList().then((res) => {
      if (res?.success) { setRows(res.data); setFiltered(res.data); }
    });
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    GetRoleList().then((res) => { if (res.status === 200) setRoles(res.data); });
    GetAgentList().then((res) => { if (res?.success) setAgentList(res.data || []); });
  }, []);

  useEffect(() => {
    GetStationList().then((res) => {
      if (res?.success) {
        const data = res.data.filter((s) => !stations.find((us) => us.id === s.id));
        setStationList(data);
      }
    });
  }, [editDialogOpen]); // eslint-disable-line

  useEffect(() => {
    getAllCustomerGroups().then((res) => {
      if (res?.success) {
        const data = (res.data || []).filter((g) => !whatsappInstances.find((wi) => wi.id === g.id));
        setInstanceList(data);
      }
    });
  }, [editDialogOpen]); // eslint-disable-line

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q
      ? rows.filter((r) => r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.role?.toLowerCase().includes(q))
      : rows
    );
  }, [search, rows]);

  const clearForm = () => {
    setName(""); setEmail(""); setWhatsapp(""); setRole("user");
    setStations([]); setWhatsappInstances([]); setRemark(""); setNewPassword(""); setConfirmNewPassword(""); setAgentId("");
  };

  const handleEdit = (user) => {
    setUserId(user._id); setName(user.name); setEmail(user.email);
    setWhatsapp(user.whatsapp); setRole(user.role); setStations(user.stations || []);
    setWhatsappInstances(user.whatsappInstances || []);
    setValidflag(user.validflag); setRemark(user.remark || "");
    setWhatsappChatFooter(user.whatsappChatFooter !== false);
    setAgentId(user.agentId || "");
    setAllowedSeeAllCustomers(!!user.allowedSeeAllCustomers);
    setAllowedSeeAllPackages(!!user.allowedSeeAllPackages);
    setEditDialogOpen(true);
  };

  const formSubmittedHandler = (e) => {
    e.preventDefault();
    PatchUser(userId, name, email, whatsapp, role, stations, remark, validflag, whatsappInstances, isAdmin ? whatsappChatFooter : undefined, role === "agent" ? agentId : undefined,
      role === "agent" ? allowedSeeAllCustomers : undefined, role === "agent" ? allowedSeeAllPackages : undefined).then((res) => {
      if (res?.success) {
        playCorrectSound();
        Swal.fire({ title: "Updated!", icon: "success", text: `${name} has been updated`, timer: 1500 });
        setEditDialogOpen(false);
        clearForm();
        loadUsers();
      } else {
        playIncorrectSound();
        Swal.fire({ title: "Error!", icon: "error", text: res?.message || "Update failed", showConfirmButton: true });
      }
    });
  };

  const formResetPasswordHandler = (e) => {
    e.preventDefault();
    if (!newPassword || newPassword !== confirmNewPassword) return;
    PatchUserPassword(userId, newPassword).then((res) => {
      if (res?.success) {
        playCorrectSound();
        Swal.fire({ title: "Updated!", icon: "success", text: "Password has been reset", timer: 1500 });
        setResetPasswordDialogOpen(false);
        setNewPassword(""); setConfirmNewPassword("");
      } else {
        playIncorrectSound();
        Swal.fire({ title: "Error!", icon: "error", text: res?.message, showConfirmButton: true });
      }
    });
  };

  const buttonAddStationHandler = () => {
    GetStationDetailById(selectedStation).then((res) => {
      if (!res?.data) return;
      const newStation = { id: res.data.id, name: res.data.name, default: stations.length === 0, parcelStatus: res.data.parcelStatus };
      setStations((prev) => [...prev, newStation]);
      setStationList((prev) => prev.filter((s) => s.id !== newStation.id));
      const remaining = stationList.filter((s) => s.id !== newStation.id);
      setSelectedStation(remaining.length > 0 ? remaining[0].id : "");
    });
  };

  const buttonRemoveStationHandler = (st) => {
    const newStations = stations.filter((s) => s.id !== st.id);
    if (st.default && newStations.length > 0) newStations[0].default = true;
    setStations(newStations);
    setStationList((prev) => [...(prev || []), st]);
  };

  const updateDefaultStation = (stationId) => {
    setStations((prev) => prev.map((s) => ({ ...s, default: s.id === stationId })));
  };

  const buttonAddInstanceHandler = () => {
    const group = (instanceList || []).find((g) => g.id === selectedInstance);
    if (!group) return;
    const newInstance = { id: group.id, name: group.name, default: whatsappInstances.length === 0 };
    setWhatsappInstances((prev) => [...prev, newInstance]);
    const remaining = instanceList.filter((g) => g.id !== newInstance.id);
    setInstanceList(remaining);
    setSelectedInstance(remaining.length > 0 ? remaining[0].id : "");
  };

  const buttonRemoveInstanceHandler = (wi) => {
    const remaining = whatsappInstances.filter((w) => w.id !== wi.id);
    if (wi.default && remaining.length > 0) remaining[0].default = true;
    setWhatsappInstances(remaining);
    setInstanceList((prev) => [...(prev || []), wi]);
  };

  const updateDefaultInstance = (instanceId) => {
    setWhatsappInstances((prev) => prev.map((w) => ({ ...w, default: w.id === instanceId })));
  };

  const dialogPaperSx = { backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary[700] : theme.palette.grey[100] };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <TextField
          size="small" sx={{ maxWidth: 320 }} placeholder="Search by name, email or role..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{filtered.length} user{filtered.length !== 1 ? "s" : ""}</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />}
          onClick={() => setNewUserDialogOpen(true)}>
          New User
        </Button>
      </Stack>

      <Dialog open={newUserDialogOpen} onClose={() => setNewUserDialogOpen(false)} maxWidth="sm" fullWidth TransitionComponent={Transition}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          New User
          <IconButton size="small" onClick={() => setNewUserDialogOpen(false)}><CloseOutlined /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <NewUser onCreated={() => { setNewUserDialogOpen(false); loadUsers(); }} />
        </DialogContent>
      </Dialog>

      <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 0.5 }}>
        <Grid container spacing={1.5}>
          {filtered.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user._id}>
              <UserCard user={user} onEdit={handleEdit} theme={theme} />
            </Grid>
          ))}
        </Grid>
        {filtered.length === 0 && (
          <Box textAlign="center" py={6} color="text.secondary">
            <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No users found.</Typography>
          </Box>
        )}
      </Box>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} maxWidth="xs" fullWidth style={{ zIndex: 1310 }}
        onClose={() => setResetPasswordDialogOpen(false)} PaperProps={{ style: dialogPaperSx }}>
        <form onSubmit={formResetPasswordHandler}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" fontWeight={700}>Reset Password</Typography>
            <IconButton onClick={() => setResetPasswordDialogOpen(false)}>
              <CloseOutlined />
            </IconButton>
          </Box>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField type="password" label="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required fullWidth size="small" />
              <TextField
                type="password" label="Confirm New Password" value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)} required fullWidth size="small"
                error={!!confirmNewPassword && newPassword !== confirmNewPassword}
                helperText={!!confirmNewPassword && newPassword !== confirmNewPassword ? "Passwords do not match" : ""}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" color="secondary" onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="secondary" type="submit" disabled={!newPassword || newPassword !== confirmNewPassword}>Reset</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} TransitionComponent={Transition} maxWidth="sm" fullWidth style={{ zIndex: 1300 }}
        onClose={() => { setEditDialogOpen(false); clearForm(); }} PaperProps={{ style: dialogPaperSx }}>
        <form onSubmit={formSubmittedHandler}>
          {/* Dialog Header with Avatar */}
          <Box sx={{ px: 3, pt: 3, pb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ width: 52, height: 52, bgcolor: getRoleStyle(role).bg, fontSize: "1.2rem", fontWeight: 700 }}>
              {getInitials(name)}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{name || "Edit User"}</Typography>
              <Chip label={getRoleStyle(role).label} size="small"
                sx={{ mt: 0.5, height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: getRoleStyle(role).bg, color: "#fff" }} />
            </Box>
            <IconButton onClick={() => setEditDialogOpen(false)} sx={{ alignSelf: "flex-start" }}>
              <CloseOutlined />
            </IconButton>
          </Box>
          <Divider />

          <DialogContent sx={{ pt: 2.5 }}>
            {/* Section: Basic Info */}
            <Typography variant="overline" color="text.secondary" fontSize="0.68rem" fontWeight={700} letterSpacing={1}>
              Basic Information
            </Typography>
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12} sm={7}>
                <FormControl fullWidth size="small">
                  <InputLabel>Name</InputLabel>
                  <OutlinedInput label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>WhatsApp</InputLabel>
                  <OutlinedInput
                    type="number" label="WhatsApp" value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)} required
                    startAdornment={<InputAdornment position="start"><WhatsAppIcon sx={{ fontSize: 16, color: "success.main" }} /></InputAdornment>}
                  />
                  <FormHelperText>e.g. 8562077790708</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Email</InputLabel>
                  <OutlinedInput
                    type="email" label="Email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    startAdornment={<InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} /></InputAdornment>}
                  />
                </FormControl>
                <Box mt={0.5}>
                  <Link component="button" variant="caption" color="secondary"
                    onClick={(e) => { e.preventDefault(); setResetPasswordDialogOpen(true); }}>
                    🔑 Reset password
                  </Link>
                </Box>
              </Grid>
            </Grid>

            {/* Section: Role & Status */}
            <Typography variant="overline" color="text.secondary" fontSize="0.68rem" fontWeight={700} letterSpacing={1} display="block" mt={2.5} mb={1}>
              Role & Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)} required>
                    {roles.map(({ role: r, description }) => (
                      <MenuItem key={r} value={r}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: getRoleStyle(r).bg, flexShrink: 0 }} />
                          <span><strong>{r.toUpperCase()}</strong> — {description}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                  <Switch color="secondary" size="small" checked={validflag || false} onChange={() => setValidflag(!validflag)} />
                </Paper>
              </Grid>
              {role === "agent" && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Agent *</InputLabel>
                    <Select value={agentId} label="Agent *" onChange={(e) => setAgentId(e.target.value)} required>
                      {agentList.map((a) => (
                        <MenuItem key={a.id} value={a.id}>{a.id} — {a.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {role === "agent" && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Can see all customers</Typography>
                      <Switch color="secondary" size="small" checked={allowedSeeAllCustomers} onChange={() => setAllowedSeeAllCustomers(!allowedSeeAllCustomers)} />
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Can see and manage all packages</Typography>
                      <Switch color="secondary" size="small" checked={allowedSeeAllPackages} onChange={() => setAllowedSeeAllPackages(!allowedSeeAllPackages)} />
                    </Stack>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Remark" value={remark} onChange={(e) => setRemark(e.target.value)} multiline maxRows={2} />
              </Grid>
            </Grid>

            {/* Section: Stations */}
            <Typography variant="overline" color="text.secondary" fontSize="0.68rem" fontWeight={700} letterSpacing={1} display="block" mt={2.5} mb={1}>
              Assigned Stations
            </Typography>
            <Stack direction="row" spacing={1} mb={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Add Station</InputLabel>
                <Select value={stationList?.length ? selectedStation : ""} label="Add Station" onChange={(e) => setSelectedStation(e.target.value)}>
                  {stationList?.map(({ id, name: n }) => <MenuItem key={id} value={id}>{id} — {n}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" color="secondary" disabled={!selectedStation} onClick={buttonAddStationHandler}
                sx={{ flexShrink: 0, px: 2.5 }}>
                Add
              </Button>
            </Stack>

            {/* Assigned stations list */}
            {stations.length > 0 && (
              <Stack spacing={0.75}>
                {stations.map((s) => (
                  <Paper key={s.id} variant="outlined" sx={{
                    px: 1.5, py: 1, borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1,
                    borderColor: s.default ? "secondary.main" : "divider",
                    bgcolor: s.default ? alpha(theme.palette.secondary.main, 0.04) : "transparent",
                  }}>
                    <Box flex={1}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                        {s.default && <Chip label="Default" size="small" color="secondary" sx={{ height: 16, fontSize: "0.62rem" }} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{s.id}</Typography>
                        </Box>
                        <Tooltip title={s.default ? "Default station" : "Set as default"}>
                          <Switch color="secondary" size="small" checked={s.default}
                            disabled={stations.length === 1} onChange={() => updateDefaultStation(s.id)} />
                        </Tooltip>
                        <IconButton size="small" onClick={() => buttonRemoveStationHandler(s)}
                          sx={{ "&:hover": { color: theme.palette.error.main } }}>
                          <ClearOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Stack>
            )}

            {stations.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: "center", borderStyle: "dashed" }}>
                <Typography variant="caption" color="text.disabled">No stations assigned yet</Typography>
              </Paper>
            )}

            {/* Section: WhatsApp Instances — same pattern as Stations above, but optional
                (not required for Save, unlike a station) since not every user needs the
                "WhatsApp Chat" menu. */}
            <Typography variant="overline" color="text.secondary" fontSize="0.68rem" fontWeight={700} letterSpacing={1} display="block" mt={2.5} mb={1}>
              WhatsApp Instances (WhatsApp Chat menu)
            </Typography>
            <Stack direction="row" spacing={1} mb={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Add Instance</InputLabel>
                <Select value={instanceList?.length ? selectedInstance : ""} label="Add Instance" onChange={(e) => setSelectedInstance(e.target.value)}>
                  {instanceList?.map(({ id, name: n }) => <MenuItem key={id} value={id}>{n}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" color="secondary" disabled={!selectedInstance} onClick={buttonAddInstanceHandler}
                sx={{ flexShrink: 0, px: 2.5 }}>
                Add
              </Button>
            </Stack>

            {whatsappInstances.length > 0 && (
              <Stack spacing={0.75}>
                {whatsappInstances.map((w) => (
                  <Paper key={w.id} variant="outlined" sx={{
                    px: 1.5, py: 1, borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1,
                    borderColor: w.default ? "secondary.main" : "divider",
                    bgcolor: w.default ? alpha(theme.palette.secondary.main, 0.04) : "transparent",
                  }}>
                    <Box flex={1}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="body2" fontWeight={600}>{w.name}</Typography>
                        {w.default && <Chip label="Default" size="small" color="secondary" sx={{ height: 16, fontSize: "0.62rem" }} />}
                      </Stack>
                    </Box>
                    <Tooltip title={w.default ? "Default instance" : "Set as default"}>
                      <Switch color="secondary" size="small" checked={w.default}
                        disabled={whatsappInstances.length === 1} onChange={() => updateDefaultInstance(w.id)} />
                    </Tooltip>
                    <IconButton size="small" onClick={() => buttonRemoveInstanceHandler(w)}
                      sx={{ "&:hover": { color: theme.palette.error.main } }}>
                      <ClearOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            )}

            {whatsappInstances.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: "center", borderStyle: "dashed" }}>
                <Typography variant="caption" color="text.disabled">No WhatsApp instances assigned — user won't see the WhatsApp Chat menu</Typography>
              </Paper>
            )}

            {isAdmin && (
              <Paper variant="outlined" sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>"Sent by" footer</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add "sent by {name || "<name>"}" under messages this user sends from WhatsApp Chat
                  </Typography>
                </Box>
                <Switch color="secondary" size="small" checked={whatsappChatFooter} onChange={() => setWhatsappChatFooter(!whatsappChatFooter)} />
              </Paper>
            )}
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="outlined" color="secondary" onClick={() => { setEditDialogOpen(false); clearForm(); }}>Cancel</Button>
            <Button variant="contained" color="secondary" type="submit"
              disabled={stations.length === 0 || !name || !whatsapp || !email}>
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UserList;
